import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  Divider,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import {
  CalendarCheck,
  CalendarX,
  UserX,
  UserPlus,
  Info,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import Loading from '../components/loading';
import ErrorMessage from '../components/errorMessage';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDashboardMetrics } from './hooks/useDashboardMetrics';

dayjs.extend(customParseFormat);
dayjs.locale('pt-br');

function MetricCard({ icon, label, value, color, suffix, tooltip, subtext, action }) {
  return (
    <Card variant="outlined" sx={{ flex: 1, minWidth: 180 }}>
      <CardContent>
        <Box display="flex" alignItems="center" gap={1.5} mb={1}>
          <Box color={color}>{icon}</Box>
          <Typography variant="body2" color="text.secondary" flex={1}>
            {label}
          </Typography>
          {tooltip && (
            <Tooltip title={tooltip} placement="top" arrow>
              <Box display="flex" color="text.disabled" sx={{ cursor: 'help' }}>
                <Info size={15} />
              </Box>
            </Tooltip>
          )}
        </Box>
        <Box display="flex" alignItems="baseline" gap={0.5}>
          <Typography variant="h3" fontWeight={700} color={color}>
            {value ?? '—'}
          </Typography>
          {suffix && value != null && (
            <Typography variant="body1" color={color} fontWeight={500}>
              {suffix}
            </Typography>
          )}
        </Box>
        {subtext && (
          <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
            {subtext}
          </Typography>
        )}
        {action && <Box mt={1}>{action}</Box>}
      </CardContent>
    </Card>
  );
}

MetricCard.propTypes = {
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.number,
  color: PropTypes.string.isRequired,
  suffix: PropTypes.string,
  tooltip: PropTypes.string,
  subtext: PropTypes.string,
  action: PropTypes.node,
};

MetricCard.defaultProps = {
  value: null,
  suffix: null,
  tooltip: null,
  subtext: null,
  action: null,
};

function SectionLabel({ children }) {
  return (
    <Typography variant="overline" color="text.secondary" display="block" mb={1.5}>
      {children}
    </Typography>
  );
}

SectionLabel.propTypes = {
  children: PropTypes.node.isRequired,
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const monthParam = dayjs(searchParams.get('month'), 'YYYY-MM', true);
    return monthParam.isValid() ? monthParam.startOf('month') : dayjs().startOf('month');
  });

  const startDate = useMemo(() => selectedMonth.startOf('month'), [selectedMonth]);
  const endDate = useMemo(() => selectedMonth.endOf('month'), [selectedMonth]);

  const { metrics, isLoading, error } = useDashboardMetrics({ startDate, endDate });
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  useEffect(() => {
    if (error) {
      setShowErrorMessage(true);
    }
  }, [error]);

  return (
    <Box p={3}>
      <Loading isToHidden={!isLoading} />
      {showErrorMessage && (
        <ErrorMessage message={error} show={showErrorMessage} setShow={setShowErrorMessage} />
      )}

      <Typography variant="h6" fontWeight={600} mb={2}>
        Dashboard
      </Typography>

      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mb={3}>
        <Typography variant="body2" color="text.secondary" whiteSpace="nowrap">
          Análise referente ao período:
        </Typography>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Tooltip title="Mês anterior" arrow>
            <IconButton
              size="small"
              onClick={() => setSelectedMonth((month) => month.subtract(1, 'month'))}
            >
              <ChevronLeft size={20} />
            </IconButton>
          </Tooltip>
          <DatePicker
            label="Mês/Ano"
            views={['year', 'month']}
            openTo="month"
            value={selectedMonth}
            onChange={(date) => {
              if (date?.isValid()) setSelectedMonth(date.startOf('month'));
            }}
            format="MM/YYYY"
            sx={{ width: 150 }}
          />
          <Tooltip title="Próximo mês" arrow>
            <IconButton
              size="small"
              onClick={() => setSelectedMonth((month) => month.add(1, 'month'))}
            >
              <ChevronRight size={20} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <SectionLabel>Consultas</SectionLabel>
      <Box display="flex" gap={2} flexWrap="wrap" mb={4}>
        <MetricCard
          icon={<CalendarCheck size={22} />}
          label="Total de Consultas"
          value={metrics.appointmentsCount}
          color="primary.main"
          tooltip="Total de consultas agendadas no período, excluindo as canceladas."
        />
        <MetricCard
          icon={<CalendarX size={22} />}
          label="Cancelamentos"
          value={metrics.canceledCount}
          color="error.main"
          tooltip="Consultas que foram canceladas dentro do período selecionado."
          action={
            <Link
              component="button"
              variant="body2"
              underline="hover"
              onClick={() =>
                navigate(
                  `/dashboard/canceled-appointments?month=${selectedMonth.format('YYYY-MM')}`,
                )
              }
            >
              Ver todas as canceladas
            </Link>
          }
        />
        <MetricCard
          icon={<UserX size={22} />}
          label="Faltas"
          value={metrics.noShowCount}
          color="text.secondary"
          tooltip="Consultas em que o paciente não compareceu e a falta foi registrada."
          action={
            <Link
              component="button"
              variant="body2"
              underline="hover"
              onClick={() =>
                navigate(`/dashboard/no-show-appointments?month=${selectedMonth.format('YYYY-MM')}`)
              }
            >
              Ver todas as faltas
            </Link>
          }
        />
      </Box>

      <SectionLabel>Pacientes</SectionLabel>
      <Box display="flex" gap={2} flexWrap="wrap">
        <MetricCard
          icon={<UserPlus size={22} />}
          label="Novos Pacientes"
          value={metrics.newPatients}
          color="success.main"
          tooltip="Pacientes cadastrados no sistema pela primeira vez dentro do período selecionado."
          subtext={
            metrics.appointmentsFromNewPatients != null
              ? `${metrics.appointmentsFromNewPatients} com consulta agendada`
              : undefined
          }
        />
      </Box>
    </Box>
  );
}
